import * as THREE from "three";

/** Seconds fully wet after leaving soak before fade begins. */
export const WET_HOLD_SEC = 2;
/** Total seconds from leaving soak until fully dry. */
export const WET_DRY_TOTAL_SEC = 7;
export const WET_FADE_SEC = WET_DRY_TOTAL_SEC - WET_HOLD_SEC;

let sharedDropTexture: THREE.Texture | null = null;

/** Soft teardrop PNG drawn once and shared by all wet pups. */
function dropTexture(): THREE.Texture {
  if (sharedDropTexture) {
    return sharedDropTexture;
  }
  const canvas = document.createElement("canvas");
  canvas.width = 16;
  canvas.height = 22;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    const cx = canvas.width * 0.5;
    const grad = ctx.createRadialGradient(cx, 7, 1, cx, 8, 8);
    grad.addColorStop(0, "rgba(190, 230, 255, 0.95)");
    grad.addColorStop(0.55, "rgba(96, 180, 230, 0.82)");
    grad.addColorStop(1, "rgba(40, 120, 190, 0.35)");
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.ellipse(cx, 7.5, 5.2, 6.4, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "rgba(120, 200, 245, 0.88)";
    ctx.beginPath();
    ctx.moveTo(cx - 2.8, 10);
    ctx.quadraticCurveTo(cx, 21.5, cx + 2.8, 10);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "rgba(255, 255, 255, 0.55)";
    ctx.beginPath();
    ctx.ellipse(cx - 1.6, 5.2, 1.5, 2.1, -0.35, 0, Math.PI * 2);
    ctx.fill();
  }
  sharedDropTexture = new THREE.CanvasTexture(canvas);
  sharedDropTexture.colorSpace = THREE.SRGBColorSpace;
  return sharedDropTexture;
}

interface Drop {
  sprite: THREE.Sprite;
  vy: number;
  life: number;
  age: number;
}

/** Dripping water sprites sliding down a soaked dog billboard. */
export class WetDropVfx {
  readonly group = new THREE.Group();
  private readonly drops: Drop[] = [];
  private spawnAcc = 0;
  private wetness = 0;

  constructor(parent: THREE.Object3D) {
    parent.add(this.group);
  }

  setWetness(w: number): void {
    this.wetness = w;
    if (w <= 0.02) {
      this.spawnAcc = 0;
    }
  }

  tick(dtSec: number, dogWidth: number, dogHeight: number): void {
    const dt = Math.min(0.05, Math.max(0.001, dtSec));
    const w = this.wetness;
    if (w > 0.04) {
      const rate = 14 + w * 34;
      this.spawnAcc += dt * rate;
      while (this.spawnAcc >= 1 && this.drops.length < 48) {
        this.spawnAcc -= 1;
        this.spawnDrop(dogWidth, dogHeight, w);
      }
    }

    const halfH = dogHeight * 0.5;
    const floorY = -halfH + dogHeight * 0.08;
    let write = 0;
    for (let i = 0; i < this.drops.length; i++) {
      const drop = this.drops[i]!;
      drop.age += dt;
      drop.sprite.position.y -= drop.vy * dt;
      const t = drop.age / drop.life;
      const mat = drop.sprite.material as THREE.SpriteMaterial;
      mat.opacity = (1 - t * t) * (0.35 + w * 0.55);
      if (drop.sprite.position.y < floorY || t >= 1) {
        this.group.remove(drop.sprite);
        drop.sprite.material.dispose();
        continue;
      }
      this.drops[write++] = drop;
    }
    this.drops.length = write;
  }

  dispose(): void {
    for (const drop of this.drops) {
      drop.sprite.material.dispose();
    }
    this.drops.length = 0;
    this.group.removeFromParent();
  }

  private spawnDrop(dogWidth: number, dogHeight: number, wetness: number): void {
    const tex = dropTexture();
    const mat = new THREE.SpriteMaterial({
      map: tex,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      opacity: 0.55 + wetness * 0.35,
    });
    const sprite = new THREE.Sprite(mat);
    const scale = 0.09 + Math.random() * 0.07;
    sprite.scale.set(scale * 0.72, scale, 1);
    sprite.renderOrder = 4;
    const halfW = dogWidth * 0.42;
    const top = dogHeight * 0.38;
    const bottom = dogHeight * 0.04;
    sprite.position.set(
      (Math.random() * 2 - 1) * halfW,
      bottom + Math.random() * (top - bottom),
      0.02,
    );
    this.group.add(sprite);
    this.drops.push({
      sprite,
      vy: 0.34 + Math.random() * 0.42 + wetness * 0.12,
      life: 0.45 + Math.random() * 0.35,
      age: 0,
    });
  }
}

/** Advance soak visual after leaving water / sprinklers. */
export function stepSoakVisual(
  soaking: boolean,
  dryElapsedSec: number,
  wasSoaked: boolean,
  dtSec: number,
): { visual: number; dryElapsedSec: number; wasSoaked: boolean } {
  const dt = Math.min(0.05, Math.max(0.001, dtSec));
  if (soaking) {
    return { visual: 1, dryElapsedSec: 0, wasSoaked: true };
  }
  if (!wasSoaked) {
    return { visual: 0, dryElapsedSec: 0, wasSoaked: false };
  }
  const elapsed = dryElapsedSec + dt;
  if (elapsed >= WET_DRY_TOTAL_SEC) {
    return { visual: 0, dryElapsedSec: 0, wasSoaked: false };
  }
  if (elapsed <= WET_HOLD_SEC) {
    return { visual: 1, dryElapsedSec: elapsed, wasSoaked: true };
  }
  const fadeT = (elapsed - WET_HOLD_SEC) / WET_FADE_SEC;
  return { visual: 1 - fadeT, dryElapsedSec: elapsed, wasSoaked: true };
}
